// src/components/modals/AddTaskModal.tsx
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Plus, Trash2 } from 'lucide-react';

const taskSchema = z.object({
  task: z.string().trim().min(1, 'Task is required'),
  subTasks: z.array(z.object({
    name: z.string().min(1, 'Sub-task name is required'),
  })).min(1, 'At least one sub-task is required'),
});

type TaskFormData = z.infer<typeof taskSchema>;

interface AddTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AddTaskModal: React.FC<AddTaskModalProps> = ({ isOpen, onClose }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue
  } = useForm<TaskFormData>({
    resolver: zodResolver(taskSchema),
    defaultValues: {
      task: '',
      subTasks: [{ name: '' }]
    }
  });

  const subTasks = watch('subTasks');

  const onSubmit = async (data: TaskFormData) => {
    try {
      console.log('Task data:', data);
      // TODO: Replace with actual API call
      // await createTask(data);
      
      reset();
      onClose();
    } catch (error) {
      console.error('Failed to create task:', error);
    }
  };

  const addSubTask = () => {
    const currentSubTasks = watch('subTasks');
    setValue('subTasks', [...currentSubTasks, { name: '' }]);
  };

  const removeSubTask = (index: number) => {
    if (subTasks.length > 1) {
      const currentSubTasks = watch('subTasks');
      setValue('subTasks', currentSubTasks.filter((_, i) => i !== index));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Task" size="sm">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 px-3">
        {/* Main Task */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Task <span className="text-red-500">*</span>
          </label>
          <Input
            {...register('task')}
            placeholder="Enter task name"
            error={errors.task?.message}
          />
        </div>
<hr />
        {/* Sub-tasks */}
        <div>

          <div className="space-y-0">
            {subTasks.map((_, index) => (
              <div key={index} className="p-0">
                <div className="flex items-center justify-between mb-1.5">
                  <h4 className="text-sm font-medium text-gray-700">
                    Sub-task #{index + 1}
                  </h4>
                 
                </div>
                
                {/* Sub-task Input */}
                <div className='flex gap-2 '>
                <Input
                  {...register(`subTasks.${index}.name`)}
                  placeholder="Enter sub-task name"
                  error={errors.subTasks?.[index]?.name?.message}
                />
                 {index !== subTasks.length - 1 ?(
                    <button
                      type="button"
                      onClick={() => removeSubTask(index)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 size={16} />
                    </button>
                  ):(
                 <span className='text-blue-600 cursor-pointer'  onClick={addSubTask}>
              <Plus/></span>  
           
                  )}
              </div>
              </div>
            ))}
          </div>
          {errors.subTasks && (
            <p className="text-xs text-red-500 mt-1">{errors.subTasks.message}</p>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button 
            variant="secondary" 
            type="button" 
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
};